// Плееры сюжет-роликов 16:9 (docs/story-videos.md). Кинематик — фуллскрин
// тёмный «остров» в PlayScreen (ролик перед уровнем / после победы): автоплей,
// всегда доступное «Пропустить»; автоплей со звуком может быть заблокирован
// браузером без жеста — тогда тап-в-плей. Модал — пересмотр из коллекции,
// нативные controls.

import { useEffect, useRef, useState } from 'react';

export function StoryCinematic({ url, volume, onDone, onFail }: {
  url: string;
  /** ролик = катсцена: канал music из настроек */
  volume: number;
  onDone: () => void;
  /** видео не загрузилось/не сыграло — уровень важнее ролика */
  onFail: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [needTap, setNeedTap] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = volume;
    // muted при нулевой громкости: браузер трактует volume=0 как «со звуком»
    // и всё равно блокирует автоплей без жеста — muted-автоплей проходит молча
    v.muted = volume <= 0;
  }, [volume]);

  useEffect(() => {
    // jsdom/старые браузеры: play() может вернуть undefined — тогда без тапа
    const p = videoRef.current?.play();
    if (p) p.catch(() => setNeedTap(true)); // автоплей заблокирован → кнопка ▶
  }, []);

  return (
    <div className="story-cinema" data-testid="story-cinema">
      <video
        ref={videoRef}
        data-testid="story-video"
        src={url}
        playsInline
        onEnded={onDone}
        onError={onFail}
      />
      {needTap && (
        <button
          type="button"
          className="story-tap"
          data-testid="story-tap"
          aria-label="Смотреть"
          onClick={() => {
            setNeedTap(false);
            videoRef.current?.play()?.catch(() => onFail());
          }}
        >
          ▶
        </button>
      )}
      <button
        type="button"
        className="btn btn-sm story-skip"
        data-testid="story-skip"
        onClick={onDone}
      >
        Пропустить ▸
      </button>
    </div>
  );
}

export function StoryModal({ title, url, volume, onClose }: {
  title: string;
  /** null — каталог ассетов недоступен (сеть): показываем заглушку */
  url: string | null;
  /** канал music из настроек — как у катсцены (иначе пересмотр громче игры) */
  volume: number;
  onClose: () => void;
}) {
  const [failed, setFailed] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (v) v.volume = volume; // не muted: в модале есть controls, звук — выбор игрока
  }, [volume, url]);

  return (
    <div
      className="story-modal"
      data-testid="story-modal"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div className="story-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="story-modal-head">
          <b>{title}</b>
          <button
            type="button"
            className="btn btn-ghost btn-round story-close"
            data-testid="story-close"
            aria-label="Закрыть"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        {url && !failed ? (
          <video
            ref={videoRef}
            data-testid="story-modal-video"
            src={url}
            controls
            autoPlay
            playsInline
            onError={() => setFailed(true)}
          />
        ) : (
          <p className="story-modal-note" data-testid="story-modal-missing">
            Ролик пока недоступен — загляни позже.
          </p>
        )}
      </div>
    </div>
  );
}
